/**
 * Tahap 8B — Attendance & Time Management + Master Kalender Kerja Indonesia (frontend).
 * Bahasa Indonesia sepenuhnya.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  CalendarDays, Clock, Plane, Timer, ClipboardCheck, ShieldCheck,
  MapPin, Camera, Check, X, Plus, Send,
} from "lucide-react";

/* ============ Master Kalender Kerja ============ */
export function CalendarPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [types, setTypes] = useState([]);
  const [newEntry, setNewEntry] = useState({ date: "", name: "", type: "NATIONAL_HOLIDAY", source: "", reference: "" });
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/hr-calendar/types").then((r) => setTypes(r.data)).catch(() => {}); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/hr-calendar/year/${year}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);
  const generateDefault = async () => {
    try {
      const r = await api.post(`/hr-calendar/generate-default/${year}`);
      toast.success(`Generated: ${r.data.inserted} baru, ${r.data.skipped} skip`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const addEntry = async () => {
    if (!newEntry.date || !newEntry.name) { toast.error("Tanggal & nama wajib"); return; }
    try {
      await api.post("/hr-calendar/entries", newEntry);
      toast.success("Entry ditambahkan");
      setNewEntry({ date: "", name: "", type: "NATIONAL_HOLIDAY", source: "", reference: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kalender Kerja Indonesia</h1>
          <p className="text-sm text-slate-600">Working days, weekend, libur nasional, cuti bersama, libur perusahaan. Editable + versioned + audit trail.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label>Tahun</Label>
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24" data-testid="cal-year" />
          <Button variant="outline" onClick={generateDefault} data-testid="cal-generate-default">Generate Default (weekend+working)</Button>
        </div>
      </div>
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        ⚠️ <b>Bukan data resmi pemerintah.</b> Default generator hanya buat Senin-Jumat WORKING_DAY + Sabtu-Minggu WEEKEND. Libur nasional & cuti bersama <b>wajib ditambahkan manual atau import</b> dari SKB 3 Menteri terbaru.
      </div>
      {data && (
        <div className="grid grid-cols-4 gap-3">
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-slate-500">Hari Kerja</div><div className="text-2xl font-bold">{data.total_working_days}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-slate-500">Total Libur</div><div className="text-2xl font-bold">{data.total_holidays}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-slate-500">Weekend</div><div className="text-2xl font-bold">{data.counts?.WEEKEND || 0}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-slate-500">Total Entry</div><div className="text-2xl font-bold">{data.total_entries}</div></CardContent></Card>
        </div>
      )}
      <Card><CardContent className="p-4 space-y-3">
        <h3 className="font-semibold text-slate-900">Tambah Entry Kalender</h3>
        <div className="grid grid-cols-5 gap-2">
          <Input type="date" value={newEntry.date} onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })} data-testid="cal-new-date" />
          <Input placeholder="Nama libur" value={newEntry.name} onChange={(e) => setNewEntry({ ...newEntry, name: e.target.value })} data-testid="cal-new-name" />
          <Select value={newEntry.type} onValueChange={(v) => setNewEntry({ ...newEntry, type: v })}>
            <SelectTrigger data-testid="cal-new-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {types.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
            </SelectContent>
          </Select>
          <Input placeholder="Sumber (mis. SKB 3 Menteri)" value={newEntry.source} onChange={(e) => setNewEntry({ ...newEntry, source: e.target.value })} data-testid="cal-new-source" />
          <Button onClick={addEntry} data-testid="btn-add-cal-entry"><Plus size={14} className="mr-1" />Tambah</Button>
        </div>
      </CardContent></Card>
      <Card><CardContent className="p-0">
        <div className="max-h-[500px] overflow-y-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Tanggal</TableHead><TableHead>Nama</TableHead>
              <TableHead>Type</TableHead><TableHead>Sumber</TableHead>
              <TableHead>Status</TableHead><TableHead>v</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={6}>Memuat…</TableCell></TableRow>
              : !data ? null
              : data.entries.filter((e) => e.type !== "WORKING_DAY").slice(0, 200).map((e) => (
                <TableRow key={e.id} data-testid={`cal-row-${e.date}`}>
                  <TableCell className="font-mono">{e.date}</TableCell>
                  <TableCell>{e.name}</TableCell>
                  <TableCell><Badge variant="outline">{e.type}</Badge></TableCell>
                  <TableCell className="text-xs">{e.source || "-"}</TableCell>
                  <TableCell><Badge>{e.status}</Badge></TableCell>
                  <TableCell>v{e.version}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>
    </div>
  );
}

/* ============ ESS Attendance Clock ============ */
export function AttendanceClockPage() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [loc, setLoc] = useState({ lat: null, lon: null, err: null });
  const [locations, setLocations] = useState([]);
  const [selectedLoc, setSelectedLoc] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/hr/work-locations").then((r) => setLocations(r.data.filter((l) => l.is_active))).catch(() => {});
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLoc({ lat: pos.coords.latitude, lon: pos.coords.longitude, err: null }),
        (err) => setLoc({ lat: null, lon: null, err: err.message }),
        { timeout: 8000 },
      );
    } else {
      setLoc({ lat: null, lon: null, err: "Browser tidak mendukung geolocation" });
    }
    loadToday();
    /* eslint-disable-next-line */
  }, []);
  const loadToday = async () => {
    try {
      const d = new Date().toISOString().slice(0, 10);
      const r = await api.get(`/hr/attendance?date_from=${d}&date_to=${d}`);
      setToday(r.data[0] || null);
    } catch (e) { /* ignore */ }
  };
  const doClock = async (action) => {
    setBusy(true);
    try {
      const body = { latitude: loc.lat, longitude: loc.lon,
                     work_location_code: selectedLoc || undefined };
      const r = await api.post(`/hr/attendance/${action}`, body);
      toast.success(`${action === "clock-in" ? "Clock-in" : "Clock-out"} sukses @ ${r.data[action === "clock-in" ? "clock_in" : "clock_out"]}`);
      loadToday();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="attendance-clock-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Absensi Hari Ini</h1>
        <p className="text-sm text-slate-600">Server timestamp = sumber waktu resmi. Face verification: <b>BELUM_TERHUBUNG</b> (provider belum aktif).</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Card><CardContent className="p-4">
          <h3 className="font-semibold mb-2 text-slate-900">Lokasi Anda</h3>
          {loc.lat != null ? (
            <div className="text-sm">
              <div><MapPin className="inline mr-1" size={14} />Lat: <span className="font-mono">{loc.lat?.toFixed(6)}</span> Lon: <span className="font-mono">{loc.lon?.toFixed(6)}</span></div>
            </div>
          ) : (
            <div className="text-sm text-rose-600">{loc.err || "Menunggu GPS…"}</div>
          )}
          <div className="mt-3">
            <Label>Lokasi Kerja</Label>
            <Select value={selectedLoc} onValueChange={setSelectedLoc}>
              <SelectTrigger data-testid="att-loc-select"><SelectValue placeholder="Pilih lokasi" /></SelectTrigger>
              <SelectContent>
                {locations.map((l) => (<SelectItem key={l.code} value={l.code}>{l.name} (r={l.radius_meters}m)</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <h3 className="font-semibold mb-2 text-slate-900">Status Hari Ini</h3>
          {today ? (
            <div className="space-y-1 text-sm">
              <div>Clock-in: <b>{today.clock_in || "-"}</b> · <Badge variant="outline">{today.clock_in_geofence || "-"}</Badge></div>
              <div>Clock-out: <b>{today.clock_out || "-"}</b> · <Badge variant="outline">{today.clock_out_geofence || "-"}</Badge></div>
              <div>Late: {today.late_minutes || 0} menit</div>
              <div>Work: {today.work_minutes || 0} menit</div>
              <div>Face: <Badge variant="outline">{today.clock_in_face_status || "-"}</Badge></div>
            </div>
          ) : <div className="text-sm text-slate-500">Belum ada absensi hari ini.</div>}
          <div className="mt-3 flex gap-2">
            <Button onClick={() => doClock("clock-in")} disabled={busy || today?.clock_in}
                    data-testid="btn-clock-in"><Clock size={16} className="mr-1" />Clock-in</Button>
            <Button onClick={() => doClock("clock-out")} disabled={busy || !today?.clock_in || today?.clock_out}
                    variant="outline" data-testid="btn-clock-out">Clock-out</Button>
          </div>
        </CardContent></Card>
      </div>
    </div>
  );
}

/* ============ Leave request ============ */
export function LeavePage() {
  const [types, setTypes] = useState([]);
  const [req, setReq] = useState({ leave_type_code: "TAHUNAN", start_date: "", end_date: "", reason: "" });
  const [my, setMy] = useState([]);
  const [balance, setBalance] = useState(null);
  useEffect(() => {
    api.get("/hr/leave-types").then((r) => setTypes(r.data)).catch(() => {});
    load();
  }, []);
  const load = async () => {
    try {
      const r = await api.get("/hr/leave-requests");
      setMy(r.data);
      const b = await api.get("/hr/leave-balance");
      setBalance(b.data);
    } catch (e) { /* ess required linked */ }
  };
  const submit = async () => {
    try {
      await api.post("/hr/leave-requests", req);
      toast.success("Cuti diajukan");
      setReq({ leave_type_code: "TAHUNAN", start_date: "", end_date: "", reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="leave-page">
      <h1 className="text-2xl font-bold text-slate-900">Cuti / Leave</h1>
      {balance && (
        <Card><CardContent className="p-4">
          <h3 className="font-semibold mb-2">Saldo Cuti Tahun {balance.year}</h3>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
            {balance.balances.map((b) => (
              <div key={b.leave_type_code} className="border rounded p-2 text-xs">
                <div className="font-semibold">{b.leave_type_name}</div>
                <div>Sisa: <b>{b.remaining_days}</b></div>
                <div>Pakai: {b.used_days} / {b.annual_entitlement}</div>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}
      <Card><CardContent className="p-4 space-y-2">
        <h3 className="font-semibold">Ajukan Cuti</h3>
        <div className="grid grid-cols-4 gap-2">
          <Select value={req.leave_type_code} onValueChange={(v) => setReq({ ...req, leave_type_code: v })}>
            <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {types.map((t) => (<SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Input type="date" value={req.start_date} onChange={(e) => setReq({ ...req, start_date: e.target.value })} data-testid="leave-start" />
          <Input type="date" value={req.end_date} onChange={(e) => setReq({ ...req, end_date: e.target.value })} data-testid="leave-end" />
          <Button onClick={submit} data-testid="btn-submit-leave"><Send size={14} className="mr-1" />Ajukan</Button>
        </div>
        <Textarea placeholder="Alasan…" value={req.reason} onChange={(e) => setReq({ ...req, reason: e.target.value })} data-testid="leave-reason" />
      </CardContent></Card>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Type</TableHead><TableHead>Periode</TableHead>
            <TableHead>Hari Kerja</TableHead><TableHead>Status</TableHead>
            <TableHead>Alasan</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {my.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-slate-500">Belum ada cuti.</TableCell></TableRow>
            : my.map((r) => (
              <TableRow key={r.id} data-testid={`leave-row-${r.id}`}>
                <TableCell>{r.leave_type_name}</TableCell>
                <TableCell>{r.start_date} → {r.end_date}</TableCell>
                <TableCell>{r.total_working_days}</TableCell>
                <TableCell><Badge>{r.status}</Badge></TableCell>
                <TableCell className="text-xs">{r.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

/* ============ Overtime ============ */
export function OvertimePage() {
  const [req, setReq] = useState({ date: "", start_time: "17:00", end_time: "20:00", reason: "" });
  const [my, setMy] = useState([]);
  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const r = await api.get("/hr/overtime-requests"); setMy(r.data); }
    catch (e) { /* ess self */ }
  };
  const submit = async () => {
    try {
      await api.post("/hr/overtime-requests", req);
      toast.success("Lembur diajukan");
      setReq({ date: "", start_time: "17:00", end_time: "20:00", reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="overtime-page">
      <h1 className="text-2xl font-bold text-slate-900">Lembur / Overtime</h1>
      <Card><CardContent className="p-4 space-y-2">
        <div className="grid grid-cols-4 gap-2">
          <Input type="date" value={req.date} onChange={(e) => setReq({ ...req, date: e.target.value })} data-testid="ot-date" />
          <Input type="time" value={req.start_time} onChange={(e) => setReq({ ...req, start_time: e.target.value })} data-testid="ot-start" />
          <Input type="time" value={req.end_time} onChange={(e) => setReq({ ...req, end_time: e.target.value })} data-testid="ot-end" />
          <Button onClick={submit} data-testid="btn-submit-ot"><Send size={14} className="mr-1" />Ajukan</Button>
        </div>
        <Textarea placeholder="Alasan lembur…" value={req.reason} onChange={(e) => setReq({ ...req, reason: e.target.value })} data-testid="ot-reason" />
      </CardContent></Card>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Tanggal</TableHead><TableHead>Waktu</TableHead>
            <TableHead>Durasi</TableHead><TableHead>Tipe</TableHead>
            <TableHead>Multiplier</TableHead><TableHead>Estimasi</TableHead>
            <TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {my.map((r) => (
              <TableRow key={r.id} data-testid={`ot-row-${r.id}`}>
                <TableCell>{r.date}</TableCell>
                <TableCell>{r.start_time} → {r.end_time}</TableCell>
                <TableCell>{r.duration_minutes} m</TableCell>
                <TableCell><Badge variant="outline">{r.day_type}</Badge></TableCell>
                <TableCell>{r.multiplier}x</TableCell>
                <TableCell>{r.estimated_amount != null ? `Rp ${Number(r.estimated_amount).toLocaleString("id-ID")}` : "-"}</TableCell>
                <TableCell><Badge>{r.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

/* ============ HR Attendance Hub / Approval ============ */
export function AttendanceHub() {
  const items = [
    { to: "/hr/kalender", icon: CalendarDays, title: "Kalender Kerja", desc: "Master kalender Indonesia" },
    { to: "/hr/absensi", icon: Clock, title: "Absensi", desc: "Clock-in/out + geofence (ESS)" },
    { to: "/hr/cuti", icon: Plane, title: "Cuti", desc: "Ajukan cuti + saldo" },
    { to: "/hr/lembur", icon: Timer, title: "Lembur", desc: "Ajukan lembur + policy multiplier" },
    { to: "/hr/koreksi", icon: ClipboardCheck, title: "Koreksi Absensi", desc: "Lupa clock in/out — approval" },
    { to: "/hr/integritas-8b", icon: ShieldCheck, title: "Integritas 8B", desc: "10 checks kalender/attendance/leave/OT" },
  ];
  return (
    <div className="space-y-4" data-testid="attendance-hub">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Absensi &amp; Waktu Kerja</h1>
        <p className="text-sm text-slate-600">Tahap 8B — kalender, shift, absensi, cuti, lembur, koreksi. Semua menghormati Master Kalender Indonesia.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => (
          <Card key={it.to} className="hover:shadow-md transition"><CardContent className="p-5">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-emerald-50 p-3"><it.icon className="text-emerald-700" size={22} /></div>
              <div className="flex-1">
                <div className="text-base font-semibold text-slate-900">{it.title}</div>
                <div className="text-sm text-slate-600 mt-1">{it.desc}</div>
                <Link to={it.to} className="mt-3 inline-block text-sm text-emerald-700 hover:underline" data-testid={`att-link-${it.title.replaceAll(" ", "-")}`}>Buka →</Link>
              </div>
            </div>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

/* ============ Correction & Integrity 8B ============ */
export function CorrectionListPage() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const r = await api.get("/hr/attendance-corrections"); setRows(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const act = async (id, action, reason) => {
    setBusy(true);
    try {
      await api.post(`/hr/attendance-corrections/${id}/${action}`, { reason });
      toast.success(`Aksi ${action} sukses`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="correction-list">
      <h1 className="text-2xl font-bold text-slate-900">Koreksi Absensi</h1>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Tgl Absensi</TableHead><TableHead>Karyawan</TableHead>
            <TableHead>Ori In/Out</TableHead><TableHead>Koreksi In/Out</TableHead>
            <TableHead>Alasan</TableHead><TableHead>Status</TableHead>
            <TableHead>Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-slate-500">Belum ada koreksi.</TableCell></TableRow>
            : rows.map((r) => (
              <TableRow key={r.id} data-testid={`corr-row-${r.id}`}>
                <TableCell>{r.attendance_date}</TableCell>
                <TableCell>{r.employee_code}</TableCell>
                <TableCell className="text-xs">{r.original_clock_in || "-"} / {r.original_clock_out || "-"}</TableCell>
                <TableCell className="text-xs">{r.corrected_clock_in || "-"} / {r.corrected_clock_out || "-"}</TableCell>
                <TableCell className="text-xs">{r.reason}</TableCell>
                <TableCell><Badge>{r.status}</Badge></TableCell>
                <TableCell>
                  {r.status === "DIAJUKAN" && (
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => act(r.id, "approve")} disabled={busy}
                              data-testid={`btn-approve-${r.id}`}><Check size={14} /></Button>
                      <Button size="sm" variant="destructive" onClick={() => {
                        const rs = prompt("Alasan tolak?"); if (rs) act(r.id, "reject", rs);
                      }} disabled={busy}
                              data-testid={`btn-reject-${r.id}`}><X size={14} /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

export function Integrity8BPage() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/hr/integrity-8b?year=${new Date().getFullYear()}`).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!d) return <div className="p-8 text-center text-slate-500">Memuat…</div>;
  return (
    <div className="space-y-4" data-testid="integrity-8b-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Integritas 8B</h1>
          <p className="text-sm text-slate-600">10 checks untuk kalender/shift/attendance/leave/overtime</p>
        </div>
        <Badge className={d.overall === "SESUAI" ? "bg-emerald-100 text-emerald-800"
                          : d.overall === "PERLU_DITINJAU" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}>
          {d.overall}
        </Badge>
      </div>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Check</TableHead><TableHead>Detail</TableHead>
            <TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.checks.map((c, i) => (
              <TableRow key={i} data-testid={`int8b-row-${i}`}>
                <TableCell>{c.name}</TableCell>
                <TableCell className="text-xs">
                  {c.count != null && `count: ${c.count}`}
                  {c.mismatch_count != null && `mismatch: ${c.mismatch_count}`}
                  {c.expected != null && ` exp: ${c.expected}`}
                  {c.actual != null && ` act: ${c.actual}`}
                  {c.note && <div className="italic text-slate-500">{c.note}</div>}
                </TableCell>
                <TableCell><Badge className={c.status === "SESUAI" ? "bg-emerald-100 text-emerald-800"
                                      : c.status === "PERLU_DITINJAU" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}>{c.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

export default {
  CalendarPage, AttendanceClockPage, LeavePage, OvertimePage,
  AttendanceHub, CorrectionListPage, Integrity8BPage,
};
